import React, { useMemo, useEffect } from 'react';
import PDF from 'assets/svgs/pdf.svg';
import { Text } from 'atoms/Text/Text';
import { Styles } from './styles';
import { AlgoliaProduct, AlgoliaIndexes, SKU } from 'models/products';
import { GridContainer, GridItem } from 'components/Grid/Grid';
import { defaultTheme } from 'constants/theme/defaultTheme';
import { ProductsProvider } from 'contexts/ProductsProvider/ProductsProvider';
import { ProductsWrapper } from '../components/ProductsWrapper/ProductsWrapper';
import { SwiperSlide } from 'swiper/react';
import { Slider } from 'features/products/components/Slider/Slider';
import { ProductThumb } from 'features/products/components/ProductThumb/ProductThumb';
import { Spacer } from 'components/Spacer/Spacer';
import { ProductsFeature } from '../components/ProductsFeature/ProductsFeature';
import { useHideIntercomLauncher } from 'hooks/useHideIntercomLauncher/useHideIntercomLauncher';
import { ProductFooter } from '../components/ProductFooter/ProductFooter';
import { ProductDisclaimer } from '../components/ProductDisclaimer/ProductDisclaimer';
import { NextImagePlaceholder } from 'features/products/components/NextImagePlaceholder/NextImagePlaceholder';
import { ProductDetailCard } from '../components/ProductDetailCard/ProductDetailCard';
import { logTrackingEvent } from 'services/analytics';
import { cleanDataAnalytics, CustomEventName } from 'services/analytics/events';
import { Button } from 'atoms/Button/Button';
import { useModal } from 'contexts/ModalProvider/ModalProvider';
import { Popup } from '../components/Popup/Popup';
import { PATHS } from 'constants/paths';
import { useRouter } from 'next/router';
import Contact from 'assets/svgs/contact.svg';
import { getProductDetailUrl } from 'utils/urlHelpers/urlHelpers';
import { ProductsContent } from 'features/products/components/ProductsContent/ProductsContent';

export type ProductDetailProps = {
  hit: AlgoliaProduct | null | undefined;
};

export type SlideItem = {
  title: string;
  url?: string;
};

export const Detail: React.FC<ProductDetailProps> = ({ hit }) => {
  const { handleModal } = useModal();
  const { push } = useRouter();
  useHideIntercomLauncher();

  const documents: SlideItem[] = [
    { title: 'Instructions For Use', url: hit?.ifu },
    { title: 'Data sheet', url: hit?.datasheet },
  ].filter(item => typeof item.url !== 'undefined');

  const analyticsData = useMemo(() => {
    if (hit) {
      return {
        productName: hit.productName,
        companyName: hit.companyName,
        categories: hit.categories.join(','),
        brandName: hit.brandName?.[0],
        areaOfSpecialisation: hit.areaOfSpecialisation,
        onVtail: hit.onVtail,
        areasOfPractice: hit.areasOfPractice?.join(','),
        skuName: hit.skuName,
        indexName: AlgoliaIndexes.Products,
        objectId: hit.objectID,
      };
    }
  }, [hit]);

  useEffect(() => {
    if (analyticsData) {
      logTrackingEvent({
        name: CustomEventName.ProductTapped,
        data: cleanDataAnalytics(analyticsData),
      });
    }
  }, [analyticsData]);

  const contactRep = () => {
    handleModal(
      <Popup
        title="Speak to your local rep?"
        description="With the vTail app you can speak to your local rep and get instant access to all the
        healthcare product information you need."
        buttonText="Sign up now"
        onClick={() => push(PATHS.ONBOARDING_WELCOME)}
        icon={() => <Contact />}
      />,
    );

    logTrackingEvent({
      name: CustomEventName.Product_Contact_Rep,
    });
  };

  const openDocument = (slide: SlideItem) => {
    logTrackingEvent({
      name: CustomEventName.Product_Open_Document,
      data: slide,
    });
    window.open(slide.url);
  };

  const openRelatedProduct = (sku: SKU) => {
    push(getProductDetailUrl(sku.urlSlug));
    logTrackingEvent({
      name: CustomEventName.Product_Open_Related,
      data: {
        skuName: sku.skuName,
      },
    });
  };

  return (
    <ProductsProvider>
      <Styles.BodyContainer>
        <ProductsWrapper>
          {hit && (
            <ProductsContent>
              <GridContainer>
                <Styles.GridImageItem xs={12} md={5} lg={6}>
                  <Styles.ImageContainer>
                    <Styles.ImageBackgroundContainer>
                      <NextImagePlaceholder
                        src={hit.skuImage}
                        height="620"
                        width="620"
                        layout="intrinsic"
                        alt={hit.productName}
                      />
                    </Styles.ImageBackgroundContainer>
                  </Styles.ImageContainer>
                </Styles.GridImageItem>
                <GridItem xs={12} md={7} lg={6}>
                  <Styles.ContentContainer>
                    <Styles.TitleContainer>
                      <Text.header responsive>{hit.productName}</Text.header>
                    </Styles.TitleContainer>
                    <Text.body2 responsive color={defaultTheme.colors.deepGrey}>
                      {hit.skuName}
                    </Text.body2>

                    <Spacer.Column numberOfSpaces={1} />
                    <Text.body2 responsive color={defaultTheme.colors.secondaryGrey}>
                      {hit.companyName}
                    </Text.body2>
                    <ProductDetailCard hit={hit} />

                    <Spacer.Column numberOfSpaces={2} />
                    <Styles.ButtonWrapper>
                      <Button text="Contact my local rep" large block onClick={contactRep} />
                    </Styles.ButtonWrapper>
                    <Spacer.Column numberOfSpaces={7} />
                    <ProductsFeature
                      productDesc={hit.productDescription}
                      features={hit.skuFeatures}
                      companyName={hit.companyName}
                      website={hit.website}
                    />
                    {documents.length >= 1 && (
                      <>
                        <Styles.TitleContainerLarge>
                          <Text.body1 responsive>Product documentation</Text.body1>
                        </Styles.TitleContainerLarge>
                        <Slider
                          breakpoints={{
                            1: { slidesPerView: 2 },
                            640: { slidesPerView: 3 },
                            900: { slidesPerView: 2 },
                            1020: { slidesPerView: 3 },
                          }}
                        >
                          {documents.map(slide => (
                            <SwiperSlide key={`${slide.title}1`}>
                              <ProductThumb
                                title={slide.title}
                                onClick={() => openDocument(slide)}
                                image={() => <PDF />}
                              />
                            </SwiperSlide>
                          ))}
                        </Slider>
                      </>
                    )}
                  </Styles.ContentContainer>
                </GridItem>
              </GridContainer>
            </ProductsContent>
          )}
          {hit && hit.skus && (
            <Styles.SkuGrid>
              <ProductsContent>
                <Styles.TitleContainerSlider>
                  <Text.body1 center>Other {hit.productName} Products</Text.body1>
                </Styles.TitleContainerSlider>
                <GridContainer>
                  <GridItem xs={12} md={12} lg={12}>
                    <Slider whiteArrowBg centerInsufficientSlides>
                      {hit.skus &&
                        hit.skus.map(sku => (
                          <SwiperSlide key={`${sku.skuName}`}>
                            <ProductThumb
                              whiteBg
                              title={sku.skuName}
                              onClick={() => openRelatedProduct(sku)}
                              image={() => (
                                <NextImagePlaceholder
                                  src={sku.skuImage}
                                  height="170"
                                  width="170"
                                  layout="intrinsic"
                                  alt={hit.productName}
                                />
                              )}
                            />
                          </SwiperSlide>
                        ))}
                    </Slider>
                  </GridItem>
                </GridContainer>
              </ProductsContent>
            </Styles.SkuGrid>
          )}
          <ProductsContent>{hit && <ProductDisclaimer product={hit} />}</ProductsContent>
        </ProductsWrapper>
        <ProductFooter />
      </Styles.BodyContainer>
    </ProductsProvider>
  );
};
